package com.dotdrives.web.pages.donations

import com.dotdrives.design.DotAvatarPalette
import com.dotdrives.design.DotTone
import com.dotdrives.model.Donation
import com.dotdrives.web.ui.DotAction
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotEmptyState
import com.dotdrives.web.ui.DotPill
import com.dotdrives.web.ui.DotSkeleton
import react.FC
import react.Key
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.span
import web.cssom.ClassName
import web.html.ButtonType
import web.html.button as buttonType

external interface DonationLedgerListProps : Props {
    var loading: Boolean
    var donations: List<Donation>
    var hasActiveFilters: Boolean
    var error: String?
    /** `role != "field"` (docs/schema.md's `has_perm_strict` write set matches the generic
     * write role check exactly). Gates "+ Record a gift" and distinguishes the RLS-hidden
     * empty state (field role) from a genuinely empty ledger. */
    var canWrite: Boolean
    var onRetry: () -> Unit
    var onOpen: (Donation) -> Unit
    var onRecordGift: () -> Unit
    var onClearFilters: () -> Unit
    var onCreateGivingForm: () -> Unit
}

val DonationLedgerList = FC<DonationLedgerListProps> { props ->
    props.error?.let { error ->
        DotBanner {
            message = error
            onRetry = props.onRetry
            className = "donations-inline-banner"
        }
    }

    when {
        props.loading && props.donations.isEmpty() -> div {
            className = ClassName("donations-ledger")
            div {
                className = ClassName("donations-ledger-head")
                listOf("GIVER", "TARGET", "SOURCE", "DATE", "QUICKBOOKS", "AMOUNT").forEach { label ->
                    div { +label }
                }
            }
            repeat(6) {
                div { className = ClassName("donations-skeleton-row"); DotSkeleton { height = "20px" } }
            }
        }

        // A field member's donations query is genuinely empty every time (has_perm_strict
        // excludes field from reads entirely) -- "No gifts recorded yet" would be an
        // affirmative false claim about org finances made to a user forbidden from seeing
        // them, not an honest empty state. This branch has to run before the
        // no-data-yet/no-results distinction below, which assumes an empty list means there
        // is really nothing there.
        !props.canWrite && props.donations.isEmpty() && props.error == null -> DotEmptyState {
            emoji = "🔒"
            title = "Donor financials aren't part of your role"
            body = "Field representatives don't have access to gift amounts or the donations ledger. Ask an admin if you need this."
            primaryAction = null
            secondaryAction = null
        }

        props.donations.isEmpty() && props.error == null -> {
            when (ledgerEmptyReason(props.hasActiveFilters)) {
                LedgerEmptyReason.NoDataYet -> DotEmptyState {
                    emoji = "💛"
                    title = "No gifts recorded yet"
                    body = "Record an offline gift, or publish a giving form and watch them arrive on their own."
                    primaryAction = DotAction("+ Record a gift", props.onRecordGift)
                    secondaryAction = DotAction("Create a giving form →", props.onCreateGivingForm)
                }
                LedgerEmptyReason.NoResultsForFilters -> DotEmptyState {
                    emoji = "🔍"
                    title = "No gifts match these filters"
                    body = "Try a different search, target, or source — or clear filters to see this month's full ledger."
                    primaryAction = DotAction("Clear filters", props.onClearFilters)
                }
            }
        }

        else -> div {
            className = ClassName("donations-ledger")
            div {
                className = ClassName("donations-ledger-head")
                listOf("GIVER", "TARGET", "SOURCE", "DATE", "QUICKBOOKS", "AMOUNT").forEach { label ->
                    div { +label }
                }
            }
            props.donations.forEach { donation ->
                val (bg, fg) = DotAvatarPalette.tintFor(donation.giverId)
                val avatarStyle = js("({})")
                avatarStyle.backgroundColor = bg
                avatarStyle.color = com.dotdrives.web.ui.accessibleAvatarText(fg)
                val status = qboStatus(donation)
                button {
                    key = donation.id.unsafeCast<Key>()
                    type = ButtonType.buttonType
                    className = ClassName(
                        "donations-row" + if (status == QboStatus.Error) " donations-row-error" else "",
                    )
                    onClick = { props.onOpen(donation) }

                    div {
                        className = ClassName("donations-row-giver")
                        div {
                            className = ClassName("donations-row-avatar")
                            style = avatarStyle
                            ariaHidden = true
                            +donationInitials(donation.giverDisplayName)
                        }
                        div {
                            div { className = ClassName("donations-row-name"); +donation.giverDisplayName }
                            div {
                                className = ClassName("donations-row-memo")
                                if (!donation.isActive) +"Inactive · imported history"
                                else +donation.memo.ifBlank { " " }
                            }
                        }
                    }
                    div { className = ClassName("donations-row-target"); +(donation.target ?: "—") }
                    div {
                        className = ClassName("donations-row-source")
                        DotPill { text = sourceLabel(donation.source); tone = sourceTone(donation.source) }
                    }
                    div { className = ClassName("donations-row-date"); +dateChip(donation.occurredAt) }
                    div {
                        className = ClassName("donations-row-qbo")
                        DotPill { text = qboStatusLabel(status); tone = qboTone(status) }
                    }
                    span {
                        className = ClassName(
                            "donations-row-amount" + if (donation.amount < 0) " donations-row-amount-negative" else "",
                        )
                        +formatMoneyFull(donation.amount)
                    }
                }
            }
        }
    }
}

fun sourceTone(source: String): DotTone = when (source) {
    "form" -> DotTone.Primary
    "everyware" -> DotTone.Info
    else -> DotTone.Neutral
}

fun qboTone(status: QboStatus): DotTone = when (status) {
    QboStatus.Synced -> DotTone.Success
    QboStatus.Error -> DotTone.Danger
    QboStatus.Queued, QboStatus.Processing -> DotTone.Warning
    QboStatus.Reconciliation -> DotTone.Danger
    else -> DotTone.Neutral
}
